import React, { useState } from 'react';
import { Clock, AlertTriangle, TrendingUp, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import InfoTooltip from '../../../components/common/InfoTooltip';

/**
 * Pipeline Aging Section
 * Shows aging matrix of sent quotes by ARR tier and days in pipeline
 *
 * @param {Object} data - Pipeline aging data with matrix and summary
 * @param {Function} onViewQuote - Callback to view a quote (receives quote id)
 */
const PipelineAgingSection = ({ data, onViewQuote }) => {
  const [expandedCell, setExpandedCell] = useState(null);

  if (!data) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Clock size={20} className="text-blue-500" />
          Pipeline Aging
        </h3>
        <div className="text-center py-8 text-slate-500">
          No pipeline aging data available
        </div>
      </div>
    );
  }

  const { summary, matrix, arrTiers, ageBuckets } = data;

  const formatCurrency = (value) => {
    if (!value) return '$0';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  // Get cell styling based on age and ARR tier
  const getCellStyle = (tier, bucket) => {
    const cell = matrix[tier]?.[bucket];
    if (!cell || cell.count === 0) return 'bg-slate-50 text-slate-400';

    // High value deals (200K+, 100K+) in older buckets are more urgent
    const isHighValue = tier === '200K+' || tier === '100K+';
    const isOld = bucket === '61-90' || bucket === '90+';
    const isVeryOld = bucket === '90+';

    if (isHighValue && isVeryOld) return 'bg-red-100 text-red-800 border-red-300';
    if (isHighValue && isOld) return 'bg-amber-100 text-amber-800 border-amber-300';
    if (isVeryOld) return 'bg-orange-50 text-orange-700 border-orange-200';
    if (isOld) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    return 'bg-green-50 text-green-700 border-green-200';
  };

  // Get row totals
  const getRowTotal = (tier) => {
    let count = 0;
    let arr = 0;
    ageBuckets.forEach(bucket => {
      const cell = matrix[tier]?.[bucket];
      if (cell) {
        count += cell.count;
        arr += cell.totalARR;
      }
    });
    return { count, arr };
  };

  // Get column totals
  const getColumnTotal = (bucket) => {
    let count = 0;
    let arr = 0;
    arrTiers.forEach(tier => {
      const cell = matrix[tier]?.[bucket];
      if (cell) {
        count += cell.count;
        arr += cell.totalARR;
      }
    });
    return { count, arr };
  };

  const toggleCell = (tier, bucket) => {
    const key = `${tier}-${bucket}`;
    setExpandedCell(expandedCell === key ? null : key);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Clock size={20} className="text-blue-500" />
          Pipeline Aging
          <span className="text-sm font-normal text-slate-500">(Days since quote generated)</span>
          <InfoTooltip
            text="Shows how long quotes have been in pipeline by ARR tier and age. Red cells indicate high-value stale deals needing attention. Click cells to see individual quotes."
            position="right"
          />
        </h3>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-50 rounded-lg p-4">
          <div className="text-sm text-slate-500 mb-1">Active Pipeline</div>
          <div className="text-2xl font-bold text-slate-800">{summary.totalDeals}</div>
          <div className="text-xs text-slate-400">quotes</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm text-blue-600 mb-1">Avg Days in Pipeline</div>
          <div className="text-2xl font-bold text-blue-800">{summary.avgDaysInPipeline}</div>
          <div className="text-xs text-blue-400">days</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-sm text-purple-600 mb-1">Weighted Avg (by ARR)</div>
          <div className="text-2xl font-bold text-purple-800">{summary.weightedAvgDays}</div>
          <div className="text-xs text-purple-400">days</div>
        </div>
        <div className={`rounded-lg p-4 ${summary.oldestDeal?.days > 90 ? 'bg-red-50' : 'bg-amber-50'}`}>
          <div className={`text-sm mb-1 ${summary.oldestDeal?.days > 90 ? 'text-red-600' : 'text-amber-600'}`}>
            Oldest Deal
          </div>
          <div className={`text-2xl font-bold ${summary.oldestDeal?.days > 90 ? 'text-red-800' : 'text-amber-800'}`}>
            {summary.oldestDeal?.days || 0} days
          </div>
          <div className={`text-xs ${summary.oldestDeal?.days > 90 ? 'text-red-400' : 'text-amber-400'}`}>
            {summary.oldestDeal ? formatCurrency(summary.oldestDeal.arr) : 'N/A'}
          </div>
        </div>
      </div>

      {/* Aging Matrix */}
      {summary.totalDeals > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left py-3 px-3 text-sm font-semibold text-slate-600 bg-slate-50 rounded-tl-lg">
                  ARR Tier
                </th>
                {ageBuckets.map((bucket, idx) => (
                  <th key={bucket} className={`text-center py-3 px-3 text-sm font-semibold bg-slate-50 ${
                    bucket === '90+' ? 'text-red-600' : bucket === '61-90' ? 'text-amber-600' : 'text-slate-600'
                  }`}>
                    {bucket} days
                    {bucket === '90+' && <AlertTriangle size={12} className="inline ml-1" />}
                  </th>
                ))}
                <th className="text-right py-3 px-3 text-sm font-semibold text-slate-600 bg-slate-50 rounded-tr-lg">
                  Row Total
                </th>
              </tr>
            </thead>
            <tbody>
              {arrTiers.map((tier, tierIdx) => {
                const rowTotal = getRowTotal(tier);
                return (
                  <React.Fragment key={tier}>
                    <tr className="border-b border-slate-100">
                      <td className={`py-3 px-3 font-medium ${
                        tier === '200K+' ? 'text-purple-700 bg-purple-50' :
                        tier === '100K+' ? 'text-blue-700 bg-blue-50' : 'text-slate-700'
                      }`}>
                        {tier}
                        {tier === '200K+' && (
                          <span className="ml-2 text-xs text-purple-500">(Deal Review)</span>
                        )}
                      </td>
                      {ageBuckets.map(bucket => {
                        const cell = matrix[tier]?.[bucket];
                        const cellKey = `${tier}-${bucket}`;
                        const isExpanded = expandedCell === cellKey;
                        const hasQuotes = cell && cell.count > 0;

                        return (
                          <td key={bucket} className="py-2 px-2">
                            <button
                              onClick={() => hasQuotes && toggleCell(tier, bucket)}
                              disabled={!hasQuotes}
                              className={`w-full p-2 rounded-lg border transition-all ${getCellStyle(tier, bucket)} ${
                                hasQuotes ? 'cursor-pointer hover:shadow-md' : 'cursor-default'
                              }`}
                            >
                              <div className="text-center">
                                <div className="font-bold text-lg">{cell?.count || 0}</div>
                                <div className="text-xs opacity-75">{formatCurrency(cell?.totalARR || 0)}</div>
                              </div>
                              {hasQuotes && (
                                <div className="flex justify-center mt-1">
                                  {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </div>
                              )}
                            </button>
                          </td>
                        );
                      })}
                      <td className="py-3 px-3 text-right bg-slate-50">
                        <div className="font-semibold text-slate-800">{rowTotal.count}</div>
                        <div className="text-xs text-slate-500">{formatCurrency(rowTotal.arr)}</div>
                      </td>
                    </tr>
                    {/* Expanded Quote Details */}
                    {ageBuckets.map(bucket => {
                      const cellKey = `${tier}-${bucket}`;
                      const cell = matrix[tier]?.[bucket];
                      if (expandedCell !== cellKey || !cell?.quotes?.length) return null;

                      return (
                        <tr key={`${cellKey}-details`}>
                          <td colSpan={ageBuckets.length + 2} className="p-0">
                            <div className="bg-slate-50 p-4 border-t border-b border-slate-200">
                              <div className="text-sm font-medium text-slate-600 mb-2">
                                {tier} deals in {bucket} days bucket:
                              </div>
                              <div className="space-y-2">
                                {cell.quotes.map(quote => (
                                  <div key={quote.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors">
                                    <div>
                                      <span className="font-medium text-slate-800">{quote.customerName}</span>
                                      <span className="text-slate-400 text-sm ml-2">#{quote.quoteNumber}</span>
                                      {quote.salesRep && (
                                        <span className="text-slate-400 text-xs ml-2">({quote.salesRep})</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="font-semibold text-blue-600">{formatCurrency(quote.arr)}</span>
                                      <span className={`text-sm px-2 py-1 rounded-full ${
                                        quote.days > 90 ? 'bg-red-100 text-red-700' :
                                        quote.days > 60 ? 'bg-amber-100 text-amber-700' :
                                        'bg-green-100 text-green-700'
                                      }`}>
                                        {quote.days} days
                                      </span>
                                      {onViewQuote && (
                                        <button
                                          onClick={() => onViewQuote(quote.id)}
                                          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                                          title="View quote"
                                        >
                                          <Eye size={14} />
                                          View
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {/* Column Totals */}
              <tr className="bg-slate-100">
                <td className="py-3 px-3 font-semibold text-slate-700">Column Total</td>
                {ageBuckets.map(bucket => {
                  const colTotal = getColumnTotal(bucket);
                  return (
                    <td key={bucket} className="py-3 px-2 text-center">
                      <div className="font-semibold text-slate-800">{colTotal.count}</div>
                      <div className="text-xs text-slate-500">{formatCurrency(colTotal.arr)}</div>
                    </td>
                  );
                })}
                <td className="py-3 px-3 text-right">
                  <div className="font-bold text-slate-800">{summary.totalDeals}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-slate-500">
          <TrendingUp size={32} className="mx-auto text-slate-300 mb-2" />
          No active pipeline quotes (approved, final, or sent)
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-green-100 border border-green-200"></span>
          Healthy (0-60 days)
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-200"></span>
          Aging (61-90 days)
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-red-100 border border-red-200"></span>
          Stale (90+ days)
        </span>
        <span className="flex items-center gap-1">
          <AlertTriangle size={12} className="text-amber-500" />
          High-value deals need review
        </span>
      </div>
    </div>
  );
};

export default PipelineAgingSection;
